import { Alert, Button, Group, PasswordInput, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { getHotkeyHandler } from '@mantine/hooks'
import { useGlobalState, useResetPasswordForm } from '@opsopti/shared'
import { IconAlertOctagon, IconCircleCheckFilled, IconCircleDashedCheck } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import type { ComponentProps } from 'react'
import { useMemo, useState } from 'react'

import type { RouteParams } from '@/configs/routes'
import { showSuccessNotification } from '@/utils/componentCommons'
import { navigateTo } from '@/utils/navigation'

type ResetPasswordScreenProps = {
  params?: RouteParams['resetPassword']
}

export const ResetPasswordScreen = observer(function ResetPasswordScreen(props: ResetPasswordScreenProps) {
  const { uiStore, api, authenticationStore } = useGlobalState()
  const { processAuthResponse } = authenticationStore
  const [authError, setAuthError] = useState<string | null>(null)

  const { form, passwordRequirements: reqs } = useResetPasswordForm({ token: props.params?.token })

  const onKeyboardEnter = useMemo(() => getHotkeyHandler([['Enter', () => void submitForm()]]), [])

  async function submitForm() {
    setAuthError(null)

    const validations = form.validate()

    if (validations.hasErrors) {
      setAuthError('Check the form for errors.')
      return
    }

    uiStore.showLoadingOverlay('relative')

    const { password, token } = form.getValues()

    const response = await api.safeRequest(api.resetPassword, { token, password })

    if (!response.ok) {
      uiStore.hideLoadingOverlay()
      setAuthError(response.message ?? 'Something went wrong.')
    } else {
      showSuccessNotification({ title: 'Password Changed!', message: 'Your password was changed successfully.' })

      const authResponse = await processAuthResponse(response)

      uiStore.hideLoadingOverlay()

      if (!authResponse.ok) {
        navigateTo('login', { searchParams: { email: response.data.user.email } })
      }
    }
  }

  const PasswordRequirementsProps: Pick<ComponentProps<typeof PasswordRequirement>, 'value' | 'initial'> = {
    value: form.getValues().password,
    initial: !form.errors.password && !form.isDirty('password'),
  }

  return (
    <Stack gap="xxl" maw={768} w="100%" px="xl">
      <Title order={6} ta="center" fw={500}>
        Reset your password:
      </Title>

      {authError && <Alert variant="light" color="red" title={authError} icon={<IconAlertOctagon />}></Alert>}

      <PasswordInput
        withAsterisk
        label="Password"
        onKeyDown={onKeyboardEnter}
        {...form.getInputProps('password')}
        error={!!form.errors.password}
      />

      <Stack>
        <PasswordRequirement {...PasswordRequirementsProps} rx={reqs.min8Rx} message="8 character minimum" />
        <PasswordRequirement {...PasswordRequirementsProps} rx={reqs.oneLowerRx} message="one lowercase character" />
        <PasswordRequirement {...PasswordRequirementsProps} rx={reqs.oneUpperRx} message="one uppercase character" />
        <PasswordRequirement {...PasswordRequirementsProps} rx={reqs.oneNumberRx} message="one number character" />
        <PasswordRequirement {...PasswordRequirementsProps} rx={reqs.oneSpecialRx} message="one special character" />
      </Stack>

      <PasswordInput
        withAsterisk
        label="Confirm Password"
        onKeyDown={onKeyboardEnter}
        {...form.getInputProps('confirmPassword')}
      />

      <Stack align="center">
        <Button size="lg" onClick={() => void submitForm()} fullWidth>
          Reset Password
        </Button>

        <Button variant="subtle" color="gray" size="compact-lg" onClick={() => navigateTo('auth')}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  )
})

function PasswordRequirement(props: { value: string; rx: RegExp; message: string; initial: boolean }) {
  const isValid = props.rx.test(props.value)
  const color = props.initial ? 'gray' : isValid ? 'green.9' : 'red'

  return (
    <Group>
      <ThemeIcon radius="xl" color={color} variant="light">
        {isValid ? <IconCircleCheckFilled /> : <IconCircleDashedCheck />}
      </ThemeIcon>

      <Text c={color}>{props.message}</Text>
    </Group>
  )
}
